import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import {
  Badge,
  Button,
  Chip,
  Field,
  FieldGrid,
  IconButton,
  InfoTip,
  SearchCombobox,
  TextInput,
} from '@zarya/ui';
import { t } from '@app/i18n';
import { ToolName } from '@app/shared/ToolName';
import type { KindResource } from './Resources.api';
import { METHODS, type ResourceFormValues, type ToolFormValues } from './ResourceModel';
import { risk, withoutEnforcement } from './resourceVocab';
import s from './Resources.module.css';

/**
 * Uma linha da aba **Tools** de T7 (FR-030, FR-032, FR-034).
 *
 * Em leitura mostra as colunas do PRD; em edição abre os campos inline (uma
 * linha por vez). Risco vazio é um valor válido — significa **desconhecido** —
 * e é o que alimenta o indicador "tools sem risco" de T6.
 */

const riskOptions = () => [
  { value: '', label: t('resources.toolRow.riskOption.unknown') },
  { value: 'read', label: t('resources.toolRow.riskOption.read') },
  { value: 'write', label: t('resources.toolRow.riskOption.write') },
  { value: 'high', label: t('resources.toolRow.riskOption.high') },
];

const OPTIONS_METHOD = METHODS.map((metodo) => ({ value: metodo, label: metodo }));

export interface ToolRowProps {
  index: number;
  /** Valores atuais da linha (observados do formulário). */
  tool: ToolFormValues;
  control: Control<ResourceFormValues>;
  errors?: FieldErrors<ToolFormValues>;
  tipo: KindResource;
  /** Resource `manual`: sem enforcement, então sem limite (FR-034). */
  manual: boolean;
  /** Tool que veio do gateway: nome e operation são somente leitura (FR-030). */
  importada: boolean;
  editavel: boolean;
  editando: boolean;
  onEditar: () => void;
  onConcluir: () => void;
  onRemover: () => void;
}

export function ToolRow({
  index,
  tool,
  control,
  errors,
  tipo,
  manual,
  importada,
  editavel,
  editando,
  onEditar,
  onConcluir,
  onRemover,
}: ToolRowProps) {
  const nome = tool.apim_name || t('resources.toolRow.noName');

  if (!editando) {
    const risco = risk(tool.risk === '' ? 'desconhecido' : tool.risk);
    return (
      <li
        className={s.toolLinha}
        data-testid={`tool-${tool.id ?? `nova-${index}`}`}
        // O nome é um só, o do gateway, e é o que o PDP vê (D2 do aim-cpj): é por ele que
        // o E2E acha a linha antes de conhecer o id.
        data-tool={tool.apim_name}
        // Risco e limites como dado, não como texto: é sobre eles que o E2E de
        // T173 afirma, e assim a asserção não quebra quando o rótulo do badge
        // mudar.
        data-risco={tool.risk === '' ? 'desconhecido' : tool.risk}
        data-limites={tool.constrainable.join(',')}
      >
        <span className={s.toolNome}>
          {/* aim-92c: a mesma forma de T5 e T10 — o Display name do gateway em destaque e o
              nome da tool embaixo; sem Display name, o nome da tool em destaque. */}
          <ToolName displayName={tool.display_name} technicalName={nome} />
        </span>
        <span className={s.toolDetalhe}>
          {tipo === 'api'
            ? `${tool.metodo ?? '—'} ${tool.path || '—'}`
            : tool.descricao || <span className={s.vazio}>—</span>}
        </span>
        <Badge tone={risco.tone}>{risco.rotulo}</Badge>
        <span className={s.toolLimites}>
          {tool.constrainable.length === 0 ? (
            <span className={s.vazio}>—</span>
          ) : (
            tool.constrainable.map((limit) => <Chip key={limit}>{limit}</Chip>)
          )}
        </span>
        <span className={s.toolAcoes}>
          {editavel ? (
            <>
              <IconButton icon="gear" label={t('resources.toolRow.edit', { name: nome })} size="sm" onClick={onEditar} />
              <IconButton
                icon="trash"
                label={t('resources.toolRow.remove', { name: nome })}
                size="sm"
                danger
                onClick={onRemover}
              />
            </>
          ) : null}
        </span>
      </li>
    );
  }

  return (
    <li className={s.toolEdicao} data-testid={`tool-${tool.id ?? `nova-${index}`}`}>
      <FieldGrid>
        <Field
          label={t('resources.toolRow.gatewayName.label')}
          span={12}
          required
          error={errors?.apim_name?.message ?? null}
          help={t(
            importada
              ? 'resources.toolRow.gatewayName.helpImported'
              : 'resources.toolRow.gatewayName.help',
          )}
        >
          {(props) => (
            <Controller
              control={control}
              name={`tools.${index}.apim_name`}
              render={({ field }) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  readOnly={importada}
                  placeholder={t('resources.toolRow.gatewayName.placeholder')}
                />
              )}
            />
          )}
        </Field>

        {tipo === 'api' ? (
          <>
            <Field
              label={t('resources.toolRow.method.label')}
              span={3}
              error={errors?.metodo?.message ?? null}
            >
              {(props) => (
                <Controller
                  control={control}
                  name={`tools.${index}.metodo`}
                  render={({ field }) => (
                    <SearchCombobox
                      id={props.id}
                      aria-describedby={props['aria-describedby']}
                      label={t('resources.toolRow.method.label')}
                      options={OPTIONS_METHOD}
                      value={field.value}
                      onChange={(valor) => field.onChange(valor)}
                      placeholder={t('resources.toolRow.method.placeholder')}
                      disabled={importada}
                    />
                  )}
                />
              )}
            </Field>

            <Field
              label={t('resources.toolRow.path.label')}
              span={8}
              help={t('resources.toolRow.path.help')}
              error={errors?.path?.message ?? null}
            >
              {(props) => (
                <Controller
                  control={control}
                  name={`tools.${index}.path`}
                  render={({ field }) => (
                    <TextInput
                      {...props}
                      {...field}
                      mono
                      readOnly={importada}
                      placeholder={t('resources.toolRow.path.placeholder')}
                    />
                  )}
                />
              )}
            </Field>
          </>
        ) : (
          <Field
            label={t('resources.toolRow.description.label')}
            span={12}
            error={errors?.descricao?.message ?? null}
          >
            {(props) => (
              <Controller
                control={control}
                name={`tools.${index}.descricao`}
                render={({ field }) => (
                  <TextInput
                    {...props}
                    {...field}
                    placeholder={t('resources.toolRow.description.placeholder')}
                  />
                )}
              />
            )}
          </Field>
        )}

        <Field
          label={t('resources.toolRow.risk.label')}
          span={6}
          tip={t('resources.toolRow.tip.risk')}
          error={errors?.risk?.message ?? null}
        >
          {(props) => (
            <Controller
              control={control}
              name={`tools.${index}.risk`}
              render={({ field }) => (
                <SearchCombobox
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  label={t('resources.toolRow.risk.label')}
                  options={riskOptions()}
                  value={field.value}
                  onChange={(valor) => field.onChange(valor ?? '')}
                  placeholder={t('resources.toolRow.risk.placeholder')}
                />
              )}
            />
          )}
        </Field>

        {/* "Aceita limite em" (constrainable) saiu do formulário (ajustes-front #3,
            28/08): o valor continua no modelo e no contrato — vem do APIM na
            importação e Access Control/políticas o usam — mas não se edita aqui.
            Em resource manual o aviso "sem enforcement" fica, porque é o que a
            pessoa precisa saber sobre a tool que está declarando. */}
        {manual ? (
          <Field label={t('resources.toolRow.enforcement.label')} span={6}>
            {(props) => (
              <span className={s.ajudaComTip} id={props.id}>
                {withoutEnforcement()}
                <InfoTip
                  text={t('resources.toolRow.enforcement.tip')}
                  label={t('resources.toolRow.enforcement.tipLabel')}
                  size="xs"
                />
              </span>
            )}
          </Field>
        ) : null}
      </FieldGrid>

      <span className={s.toolEdicaoAcoes}>
        <Button variant="out" size="sm" onClick={onConcluir}>
          {t('resources.toolRow.done')}
        </Button>
      </span>
    </li>
  );
}
